import { Media } from '@capacitor-community/media';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { getNativePlatform, isNativePlatform, isNativePluginAvailable, waitForNativePlugin } from './capacitor';

export type DownloadBlobResult = 'saved' | 'canceled';
export type ShareBlobResult = 'shared' | 'canceled';

const PLUGIN_WAIT_MS = 1200;
const ANDROID_ALBUM_NAME = 'Sporto';
const ACCESS_DENIED_MESSAGE =
  'Izin galeri ditolak. Buka Pengaturan untuk mengizinkan Sporto menyimpan gambar.';
const SAVE_FAILED_MESSAGE = 'Gagal menyimpan ke Galeri. Coba lagi.';

function sanitizeFilename(filename: string): string {
  const trimmed = filename.trim() || 'download.bin';
  return trimmed.replace(/[/\\?%*:|"<>]/g, '_');
}

function filenameWithoutExtension(filename: string): string {
  const lastDot = filename.lastIndexOf('.');
  if (lastDot <= 0) {
    return filename;
  }
  return filename.slice(0, lastDot);
}

function isAccessDenied(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const err = error as { code?: string; message?: string };
  if (err.code === 'accessDenied') {
    return true;
  }
  const message = (err.message ?? '').toLowerCase();
  return message.includes('accessdenied') || message.includes('access denied');
}

function wrapMediaError(error: unknown): Error {
  if (isAccessDenied(error)) {
    return new Error(ACCESS_DENIED_MESSAGE);
  }
  return new Error(SAVE_FAILED_MESSAGE);
}

export function isShareCanceled(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const err = error as { name?: string; message?: string; code?: string };
  if (err.name === 'AbortError') {
    return true;
  }
  const message = (err.message ?? '').toLowerCase();
  return message.includes('share canceled') || message.includes('cancel') || err.code === 'CANCELED';
}

async function blobToDataUri(blob: Blob): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('Gagal membaca file.'));
    reader.readAsDataURL(blob);
  });
  if (!dataUrl) {
    throw new Error('Gagal membaca file.');
  }
  return dataUrl;
}

function dataUriToBase64(dataUri: string): string {
  const comma = dataUri.indexOf(',');
  return comma >= 0 ? dataUri.slice(comma + 1) : dataUri;
}

function downloadViaAnchor(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

async function pluginReady(pluginName: string): Promise<boolean> {
  if (isNativePluginAvailable(pluginName)) {
    return true;
  }
  return waitForNativePlugin(pluginName, PLUGIN_WAIT_MS);
}

async function resolveAndroidAlbumIdentifier(): Promise<string> {
  const albumsPath = (await Media.getAlbumsPath()).path;
  const findAlbum = (albums: Array<{ name: string; identifier: string }>) =>
    albums.find(
      (album) => album.name === ANDROID_ALBUM_NAME && album.identifier.startsWith(albumsPath)
    );

  const existing = findAlbum((await Media.getAlbums()).albums);
  if (existing) {
    return existing.identifier;
  }

  await Media.createAlbum({ name: ANDROID_ALBUM_NAME });
  const created = findAlbum((await Media.getAlbums()).albums);
  if (!created) {
    throw new Error(SAVE_FAILED_MESSAGE);
  }
  return created.identifier;
}

async function saveViaMedia(blob: Blob, filename: string): Promise<void> {
  const dataUri = await blobToDataUri(blob);
  const platform = getNativePlatform();

  try {
    if (platform === 'android') {
      const albumIdentifier = await resolveAndroidAlbumIdentifier();
      await Media.savePhoto({
        path: dataUri,
        albumIdentifier,
        fileName: filenameWithoutExtension(filename),
      });
      return;
    }

    await Media.savePhoto({ path: dataUri });
  } catch (error) {
    throw wrapMediaError(error);
  }
}

async function saveViaDocuments(blob: Blob, filename: string): Promise<void> {
  const dataUri = await blobToDataUri(blob);
  try {
    await Filesystem.writeFile({
      path: `sporto-download/${filename}`,
      data: dataUriToBase64(dataUri),
      directory: Directory.Documents,
      recursive: true,
    });
  } catch {
    throw new Error(SAVE_FAILED_MESSAGE);
  }
}

/**
 * Simpan blob sebagai file. Di browser memakai `<a download>`.
 * Di app native, gambar disimpan ke Galeri/Photos lewat plugin Media
 * (hanya dipanggil saat user tap Download). APK lama tanpa plugin
 * menulis ke Documents. Tidak membuka share sheet.
 */
export async function downloadOrShareBlob(
  blob: Blob,
  filename: string,
  _options?: { title?: string; dialogTitle?: string }
): Promise<DownloadBlobResult> {
  if (!blob || blob.size === 0) {
    throw new Error('File kosong. Coba unduh lagi.');
  }

  const safeName = sanitizeFilename(filename);

  if (isNativePlatform()) {
    const isImage = /\.(png|jpe?g|webp|gif|bmp)$/i.test(safeName);
    if (isImage && (await pluginReady('Media'))) {
      await saveViaMedia(blob, safeName);
      return 'saved';
    }

    if (await pluginReady('Filesystem')) {
      await saveViaDocuments(blob, safeName);
      return 'saved';
    }

    throw new Error(SAVE_FAILED_MESSAGE);
  }

  downloadViaAnchor(blob, safeName);
  return 'saved';
}

async function shareViaNativePlugins(
  blob: Blob,
  filename: string,
  options?: { title?: string; dialogTitle?: string }
): Promise<void> {
  const dataUri = await blobToDataUri(blob);
  const written = await Filesystem.writeFile({
    path: `sporto-share/${filename}`,
    data: dataUriToBase64(dataUri),
    directory: Directory.Cache,
    recursive: true,
  });

  await Share.share({
    title: options?.title ?? filename,
    dialogTitle: options?.dialogTitle ?? 'Bagikan gambar',
    files: [written.uri],
  });
}

async function shareViaWebApi(
  blob: Blob,
  filename: string,
  title?: string
): Promise<'shared' | 'canceled' | 'unavailable'> {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') {
    return 'unavailable';
  }

  const file = new File([blob], filename, { type: blob.type || 'image/png' });
  if (typeof navigator.canShare === 'function' && !navigator.canShare({ files: [file] })) {
    return 'unavailable';
  }

  try {
    await navigator.share({ files: [file], title: title ?? filename });
    return 'shared';
  } catch (error) {
    if (isShareCanceled(error)) {
      return 'canceled';
    }
    return 'unavailable';
  }
}

/**
 * Buka share sheet native / Web Share API. Tidak menyimpan ke Galeri
 * dan tidak jatuh ke `<a download>` — itu tugas tombol Download.
 */
export async function shareBlob(
  blob: Blob,
  filename: string,
  options?: { title?: string; dialogTitle?: string }
): Promise<ShareBlobResult> {
  if (!blob || blob.size === 0) {
    throw new Error('File kosong. Coba bagikan lagi.');
  }

  const safeName = sanitizeFilename(filename);

  if (isNativePlatform()) {
    const shareReady = await pluginReady('Share');
    const filesystemReady = await pluginReady('Filesystem');
    if (shareReady && filesystemReady) {
      try {
        await shareViaNativePlugins(blob, safeName, options);
        return 'shared';
      } catch (error) {
        if (isShareCanceled(error)) {
          return 'canceled';
        }
        throw new Error('Gagal membagikan gambar. Coba lagi.');
      }
    }
  }

  const webShare = await shareViaWebApi(blob, safeName, options?.title);
  if (webShare === 'shared' || webShare === 'canceled') {
    return webShare;
  }

  throw new Error('Peramban tidak mendukung berbagi. Gunakan tombol Download.');
}
